import { memo, useMemo } from 'react';
import { AlertBanner, Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { MEMBER_STATUS } from '../../constants/copy.js';
import { INVITATION_VALID_DAYS } from '../../constants/memberDetail.js';
import ImportDialog from '../shared/ImportDialog.jsx';
import MemberAvatar from '../shared/MemberAvatar.jsx';

const NAMES_SHOWN = 6;
const hasAccess = (member) => member.roles.length > 0 || member.userType === 'admin' || member.userType === 'owner';

/**
 * "Invite N members?": checks who is included before the emails go out (they go at once and
 * cannot be recalled) and warns about the people invited with no access. Mount it only while open.
 *
 * @param {object} props
 * @param {object[]} props.members UI members about to be invited.
 * @param {boolean} props.isBusy
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onClose
 */
function SendInvitationsConfirm({ members, isBusy, onConfirm, onClose }) {
  const { t } = useTranslation('people-access');
  const noAccess = useMemo(() => members.filter((member) => !hasAccess(member)), [members]);
  const names = noAccess.slice(0, NAMES_SHOWN).map((member) => member.displayName);
  const rest = noAccess.length - names.length;
  const listed = rest > 0 ? `${names.join(', ')} ${t('members.sendInvitations.more', { count: rest })}` : names.join(', ');

  return (
    <ImportDialog
      onClose={onClose}
      isBusy={isBusy}
      titleId="send-invitations-title"
      title={t('members.sendInvitations.title', { count: members.length })}
      footer={
        <>
          <Button variant="secondary" customButtonHeight="!h-[40px]" className="!px-6" data-autofocus disabled={isBusy} onClick={onClose}>
            {t('import.cancel')}
          </Button>
          <Button variant="primary" customButtonHeight="!h-[40px]" className="!px-6" disabled={isBusy} onClick={onConfirm}>
            {t('members.sendInvitations.confirm', { count: members.length })}
          </Button>
        </>
      }
    >
      <p className="text-sm text-content-secondary">{t('members.sendInvitations.message', { days: INVITATION_VALID_DAYS })}</p>
      <ul className="m-0 flex max-h-[40vh] list-none flex-col overflow-y-auto p-0 scrollbar-aimsos-auto">
        {members.map((member) => (
          <li key={member.id} className="flex items-center justify-between gap-3 border-t border-edge py-2">
            <div className="flex min-w-0 items-center gap-3">
              <MemberAvatar member={member} size="md" />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-content">{member.displayName}</span>
                <span className="truncate text-xs text-content-secondary">{member.email}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Badge size="small" variant={hasAccess(member) ? 'success' : 'neutral'}>
                {hasAccess(member) ? t('members.sendInvitations.hasAccess') : t('members.sendInvitations.noAccess')}
              </Badge>
              <span className="text-xs text-content-muted">
                {member.status === MEMBER_STATUS.NOT_INVITED
                  ? t('members.sendInvitations.first')
                  : t('members.sendInvitations.again')}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {noAccess.length > 0 ? (
        <AlertBanner tone="warning" title={t('members.sendInvitations.noAccessTitle', { count: noAccess.length })}>
          {t('members.sendInvitations.noAccessMessage', { names: listed })}
        </AlertBanner>
      ) : null}
    </ImportDialog>
  );
}

export default memo(SendInvitationsConfirm);
